import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";
import { axiosRDV } from "../../../api/axios";
import { DEFAULT_ACCESS_SCOPES } from "../../../authorization/useMedicalCapabilities";
import { DOCTOR_ROUTES } from "../../../routes/patientRoutes/routePaths";
import { useTranslation } from "react-i18next";
import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";
import useCabinetDashboard from "../../hooks/useCabinetDashboard";
import CalendarGrid from "../../components/CalendarGrid/CalendarGrid";
import OverviewCards from "../../components/OverviewCards/OverviewCards";
import QuickActions from "../../components/QuickActions/QuickActions";
import DashboardWidgets from "../../components/DashboardWidgets/DashboardWidgets";
import BookDashboard from "../../components/BookDashboard/BookDashboard";
import GreetingHeader from "../../components/GreetingHeader/GreetingHeader";
import { updateRoomStatus } from "../../../WaitingRoom/redux/thunks/room.thunks";
import "./CabinetDashboard.css";

export default function CabinetDashboard({ LayoutComponent = AppLayoutDoctor, medecinIdOverride = null }) {
  const { t } = useTranslation("cabinetDashboard");
  const doctor = useSelector((state) => state.doctor?.user);
  const {
    today,
    anchorDate,
    viewMode,
    weekDates,
    stats,
    appointments,
    allAppointments,
    calendarLoading,
    rooms,
    waitingRooms,
    roomsReady,
    consultations,
    lastUpdatedAt,
    accessByPatient,
    blockedSlots,
    workingPeriods,
    medecinId,
    confirmAppointment,
    bookAppointment,
    goBack,
    goForward,
    changeViewMode,
  } = useCabinetDashboard({ medecinIdOverride });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasMountedRef = useRef(false);
  const previousAppointmentIds = useRef(null);
  const previousWaitingRoomIds = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [appointmentType, setAppointmentType] = useState(null);
  const [prefilledDate, setPrefilledDate] = useState(null);
  const [prefilledSlot, setPrefilledSlot] = useState(null);
  const [animateWelcome] = useState(() => {
    const shouldAnimate = sessionStorage.getItem("dashboardWelcomeAnimation") === "true";
    if (shouldAnimate) sessionStorage.removeItem("dashboardWelcomeAnimation");
    return shouldAnimate;
  });

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (calendarLoading || !roomsReady) return;

    const appointmentIds = new Set(
      allAppointments.map((appointment) => String(appointment.id || appointment.rdvId))
    );
    const waitingRoomIds = new Set(waitingRooms.map((room) => String(room.roomId || room.id)));

    if (previousAppointmentIds.current === null) {
      previousAppointmentIds.current = appointmentIds;
      previousWaitingRoomIds.current = waitingRoomIds;
      return;
    }

    const hasNewAppointment = [...appointmentIds].some((id) => !previousAppointmentIds.current.has(id));
    const hasNewWaitingPatient = [...waitingRoomIds].some((id) => !previousWaitingRoomIds.current.has(id));

    if (hasNewAppointment) {
      message.info(t("dashboard.widgets.newAppointment", { defaultValue: "Un nouveau rendez-vous a été ajouté." }));
    }
    if (hasNewWaitingPatient) {
      message.info(t("dashboard.widgets.newWaitingPatient", { defaultValue: "Un patient vient d'arriver en salle d'attente." }));
    }

    previousAppointmentIds.current = appointmentIds;
    previousWaitingRoomIds.current = waitingRoomIds;
  }, [calendarLoading, roomsReady, allAppointments, waitingRooms, t]);

  const handleCallPatient = async (room) => {
    const roomId = room?.roomId || room?.id;
    if (!roomId) return;
    try {
      await dispatch(updateRoomStatus({ roomId, status: "in_consultation" })).unwrap();
      message.success(t("dashboard.widgets.patientCalled", { defaultValue: "Le patient a été appelé." }));
    } catch (error) {
      console.error("Unable to call waiting patient", error);
      message.error(t("dashboard.widgets.callFailed", { defaultValue: "Impossible d'appeler ce patient." }));
    }
  };

  const handleStartConsultation = async (appointment) => {
    const appointmentId = String(appointment?.rdvId || appointment?.id || "");
    const room = rooms.find((item) =>
      String(item?.appointmentId || item?.rendezvousId || "") === appointmentId
    );
    if (room) {
      await handleCallPatient(room);
    }
    navigate("/waiting-room");
  };

  const openAppointmentPatient = async (appointment) => {
    const patientId = String(appointment?.patientId ?? "").trim();
    if (!patientId) return;
    if (appointment.accessAllowed || accessByPatient?.[patientId]?.allowProfile) {
      navigate(DOCTOR_ROUTES.DOCTOR_PATIENT_VIEW(patientId));
      return;
    }

    const subprofileId = String(appointment?.subprofileId || "").trim();
    const targetId = String(
      appointment?.effectiveProfileId || subprofileId || patientId,
    ).trim();
    const query = new URLSearchParams({ patientId });
    if (subprofileId) query.set("subprofileId", subprofileId);

    navigate(`${DOCTOR_ROUTES.FILE_PAGE(targetId)}?${query.toString()}`);
  };

  const openBookingModal = (type = 'new', date = null, hour = null, minute = 0) => {
    setAppointmentType(type);
    if (type === 'add' && date && hour !== null) {
      setPrefilledDate(date);
      const startTime = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, minute || 0);
      const endTime = new Date(startTime.getTime() + 15 * 60 * 1000); // 15 minutes slot
      setPrefilledSlot({
        id: `static-${String(hour).padStart(2, '0')}:${String(minute || 0).padStart(2, '0')}`,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        source: "static",
      });
    } else {
      setPrefilledDate(null);
      setPrefilledSlot(null);
    }
    setShowModal(true);
  };

  const closeBookingModal = () => {
    setShowModal(false);
    setAppointmentType(null);
    setPrefilledDate(null);
    setPrefilledSlot(null);
  };

  return (
    <LayoutComponent>
      <div className="cd">
        <GreetingHeader
          doctorName={doctor?.fullName || `${doctor?.firstName || ""} ${doctor?.lastName || ""}`.trim()}
          appointments={allAppointments}
          animateWelcome={animateWelcome}
        />

        <QuickActions onNewAppointment={() => openBookingModal("new")} />

        <div className="cd__grid">
          <CalendarGrid
            weekDates={weekDates}
            viewMode={viewMode}
            today={today}
            anchorDate={anchorDate}
            appointments={appointments}
            blockedSlots={blockedSlots}
            workingPeriods={workingPeriods}
            onConfirmAppointment={confirmAppointment}
            onOpenAppointment={openAppointmentPatient}
            onBack={goBack}
            onForward={goForward}
            onChangeViewMode={changeViewMode}
            onNewAppointment={openBookingModal}
            loading={calendarLoading}
          />
          <aside className="cd__sidebar">
            <OverviewCards stats={stats} />
            <DashboardWidgets
              appointments={allAppointments}
              waitingRooms={waitingRooms}
              consultations={consultations}
              lastUpdatedAt={lastUpdatedAt}
              onCallPatient={handleCallPatient}
              onOpenPatient={openAppointmentPatient}
              onStartConsultation={handleStartConsultation}
            />
          </aside>
        </div>
      </div>

      {showModal && (
        <BookDashboard
          medecinId={medecinId}
          workingPeriods={workingPeriods}
          appointmentType={appointmentType}
          prefilledDate={prefilledDate}
          prefilledSlot={prefilledSlot}
          onClose={closeBookingModal}
          onBooked={(booked, patient, selectedDate, selectedSlot, reason) =>
            bookAppointment({ booked, patient, selectedDate, selectedSlot, reason })
          }
        />
      )}
    </LayoutComponent>
  );
}
